/* CHAMPIONSHIP BRACKET — broadcast-grade tournament tree under the arena screen.
   True bracket geometry: semifinals center exactly between their feeder
   quarterfinals, the final centers across the tree, and the champion box closes
   it. Elbow connectors ignite amber for the winner's path and signal red for
   yours — and when a match resolves, the winner's emblem visibly TRAVELS the
   elbow into the next round on a rising whoosh (cascading on a full sim). A
   brass sting + light sweep open the round-of-8 ceremony; a crowd roar answers
   the coronation. Back-to-back reigns and dynasty chips get the DYNASTY HORN —
   a deep air-horn under a flaming crown. Under 640px the tree collapses into a
   round tab switcher. */
import { motion } from "framer-motion";
import { Crown, Flame, Trophy, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { API, TEAMS, teamToFranchise } from "./riotball/constants";
import { PLAYOFF_ROUNDS, playerMatch } from "./riotball/playoff";
import { playHorn, playRoar, playSting, playWhoosh } from "./riotball/bracketAudio";

const ROUND_SLUGS = ["play-in", "quarterfinals", "semifinals", "finals"];
const ROUND_CLASSES = ["playoff-round-pi", "playoff-round-qf", "playoff-round-sf", "playoff-round-f"];
const ROUND_TICKS = ["[01–02]", "[03–06]", "[07–08]", "[09]"];
const TAB_LABELS = [...PLAYOFF_ROUNDS, "CHAMPION"];

/* same law as the engine — the winner's seed line beats the favorite by 3+ */
const isUpset = (m) => {
  if (!m.played || !m.winner || !m.homeSeed || !m.awaySeed) return false;
  const wSeed = m.winner === m.home ? m.homeSeed : m.awaySeed;
  const lSeed = m.winner === m.home ? m.awaySeed : m.homeSeed;
  return wSeed - lSeed >= 3;
};

const TeamRow = ({ t, seed, score, played, won, isYou }) => (
  <span
    className={`playoff-team${won ? " won" : played ? " lost" : ""}${isYou ? " is-you" : ""}`}
    data-testid="playoff-team-row"
  >
    <i style={{ background: TEAMS[t]?.color }} />
    <b className="playoff-seed">#{seed ?? "–"}</b>
    <img className="playoff-team-emblem" src={TEAMS[t]?.emblem} alt="" loading="lazy" />
    <span className="playoff-team-name">{TEAMS[t]?.name || String(t).toUpperCase()}</span>
    <em className="playoff-score">{played ? score : "–"}</em>
  </span>
);

const MatchCard = ({ m, liveId, playerTeam }) => {
  const live = m.id === liveId;
  const upset = isUpset(m);
  return (
    <div
      className={`playoff-match${m.isPlayer ? " is-player" : ""}${live ? " is-live" : ""}${upset ? " is-upset" : ""}${m.played ? " is-played" : ""}`}
      data-testid={`playoff-match-${m.id}`}
    >
      <span className="playoff-link" aria-hidden="true" />
      <span className="playoff-match-state">{live ? "● LIVE MATCH" : m.played ? "FINAL" : "UPCOMING"}</span>
      <TeamRow t={m.home} seed={m.homeSeed} score={m.homeScore} played={m.played} won={m.winner === m.home} isYou={m.home === playerTeam} />
      <TeamRow t={m.away} seed={m.awaySeed} score={m.awayScore} played={m.played} won={m.winner === m.away} isYou={m.away === playerTeam} />
      {upset ? (
        <span className="playoff-upset-badge" data-testid="playoff-upset-badge">
          <Zap size={9} /> UPSET
        </span>
      ) : null}
    </div>
  );
};

const ChampionColumn = ({ p, active, flames, flameLabel }) => (
  <div className={`playoff-round playoff-champ-col${p.finished ? "" : " is-waiting"}${active ? " is-tab-active" : ""}`} data-testid="playoff-round-champion">
    <span className="playoff-round-label">// CHAMPION <b>[CHIP]</b></span>
    {p.finished && p.champion ? (
      <motion.div
        className={`playoff-champ-box is-crowned${flames ? " is-dynasty" : ""}`}
        data-testid="playoff-champion"
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: [0.85, 1.05, 1] }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        {flames ? (
          <span className="playoff-dynasty-flames" aria-hidden="true" data-testid="playoff-dynasty-flames">
            <i className="f1" /><i className="f2" /><i className="f3" /><i className="f4" /><i className="f5" />
          </span>
        ) : null}
        <span className="playoff-spark sp1" aria-hidden="true" />
        <span className="playoff-spark sp2" aria-hidden="true" />
        <span className="playoff-spark sp3" aria-hidden="true" />
        <span className="playoff-spark sp4" aria-hidden="true" />
        {flames ? <Crown size={30} /> : <Trophy size={30} />}
        {flames ? <Flame size={14} aria-hidden="true" /> : null}
        <strong style={{ color: TEAMS[p.champion]?.color }}>{TEAMS[p.champion]?.name}</strong>
        <em>{p.champion === p.playerTeam ? "YOU TAKE THE CHIP" : "LEAGUE CHAMPION"}</em>
        {flameLabel ? <em className="playoff-dynasty-tag" data-testid="playoff-dynasty-tag">{flameLabel}</em> : null}
      </motion.div>
    ) : (
      <div className="playoff-champ-box" data-testid="playoff-champion">
        <Trophy size={22} />
        <strong>TO BE DECIDED</strong>
        <em>THE CHIP AWAITS</em>
      </div>
    )}
  </div>
);

/* a winner in flight — emblem rides the elbow: out of the settled match, up or
   down the gap midpoint, into next round's card (or the champion box) */
const Traveler = ({ t, playerTeam }) => (
  <motion.span
    className={`playoff-traveler${t.team === playerTeam ? " is-you" : ""}`}
    style={{ "--tcolor": TEAMS[t.team]?.color }}
    initial={{ x: t.x0, y: t.y0, opacity: 0, scale: 0.5 }}
    animate={{
      x: [t.x0, t.x1, t.x1, t.x2],
      y: [t.y0, t.y0, t.y2, t.y2],
      opacity: [0, 1, 1, 0.85],
      scale: [0.5, 1.05, 1.05, 0.6],
    }}
    transition={{ duration: 0.85, delay: t.delay, times: [0, 0.3, 0.68, 1], ease: "easeInOut" }}
    data-testid={`playoff-traveler-${t.key}`}
  >
    <img src={TEAMS[t.team]?.emblem} alt="" />
  </motion.span>
);

export function RiotballPlayoffBracket({ playoff: p, coronation }) {
  const [tab, setTab] = useState(p.roundIdx);
  const [travelers, setTravelers] = useState([]);
  const [ceremony, setCeremony] = useState(true);
  const bracketRef = useRef(null);
  const prevRef = useRef(null);
  const reigningRef = useRef(null);
  const hornedRef = useRef(false);
  const live = playerMatch(p);

  /* play-in ceremony — mounting IS a playoff run starting: sting + sweep.
     Also note the reigning champion (last recorded bracket) for the horn. */
  useEffect(() => {
    playSting();
    fetch(`${API}/riotball/playoff-champions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        reigningRef.current = d?.champions?.[0]?.team || null;
      })
      .catch(() => {});
    const t = window.setTimeout(() => setCeremony(false), 1700);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = p;
    const box = bracketRef.current;
    if (!prev || !box) return undefined;
    /* matches that JUST resolved — their winners ride the connectors */
    const fresh = [];
    p.rounds.forEach((round, ri) => {
      const prevRound = prev.rounds?.[ri] || [];
      round.forEach((m, mi) => {
        if (!m.played || !m.winner) return;
        const was = prevRound.find((pm) => pm.id === m.id);
        if (was?.played) return;
        fresh.push({ m, ri, mi });
      });
    });
    if (!fresh.length) return undefined;
    const boxRect = box.getBoundingClientRect();
    const next = [];
    for (const { m, ri, mi } of fresh) {
      const src = box.querySelector(`[data-testid="playoff-match-${m.id}"]`);
      if (!src) continue;
      let dst = null;
      if (ri < p.rounds.length - 1) {
        const destMatch = p.rounds[ri + 1].find((nm) => nm.home === m.winner || nm.away === m.winner);
        if (destMatch) dst = box.querySelector(`[data-testid="playoff-match-${destMatch.id}"]`);
      } else {
        dst = box.querySelector('[data-testid="playoff-champion"]');
      }
      if (!dst) continue;
      const s = src.getBoundingClientRect();
      const d = dst.getBoundingClientRect();
      next.push({
        key: `${m.id}-${m.winner}`,
        team: m.winner,
        round: ri,
        x0: s.right - boxRect.left,
        y0: s.top + s.height / 2 - boxRect.top,
        x1: (s.right + d.left) / 2 - boxRect.left,
        x2: d.left - boxRect.left + 8,
        y2: d.top + d.height / 2 - boxRect.top,
        delay: ri * 0.45 + mi * 0.12,
      });
    }
    if (!next.length) return undefined;
    /* flight cues — one whoosh per round in the cascade, timed to its wave;
       a final-round traveler means a coronation: the arena erupts on docking */
    [...new Set(next.map((t) => t.round))].forEach((ri) => playWhoosh(ri * 0.45));
    if (next.some((t) => t.round === p.rounds.length - 1)) playRoar(((p.rounds.length - 1) * 0.45 + 0.75) * 1000);
    setTravelers(next);
    const maxDelay = Math.max(...next.map((t) => t.delay));
    const timer = window.setTimeout(() => setTravelers([]), (maxDelay + 1.15) * 1000);
    return () => window.clearTimeout(timer);
  }, [p]);

  /* the DYNASTY HORN — flames when the player mints a dynasty/back-to-back chip,
     or when a simmed bracket extends the reigning franchise's reign */
  const playerFlames = Boolean(
    p.finished && p.champion === p.playerTeam && coronation && (coronation.dynasty || coronation.backToBack)
  );
  const simFlames = Boolean(
    p.finished &&
      p.champion &&
      p.champion !== p.playerTeam &&
      reigningRef.current &&
      teamToFranchise(p.champion) === reigningRef.current
  );
  const flames = playerFlames || simFlames;
  const flameLabel =
    playerFlames && coronation?.dynasty
      ? "DYNASTY FORGED — THREE CHIPS, ONE FRANCHISE"
      : flames
        ? "BACK-TO-BACK — THE REIGN CONTINUES"
        : null;

  useEffect(() => {
    if (flames && !hornedRef.current) {
      hornedRef.current = true;
      playHorn();
    }
  }, [flames]);

  const status = p.finished
    ? "CHAMPION CROWNED — BRACKET COMPLETE"
    : p.playerAlive
      ? `${PLAYOFF_ROUNDS[p.roundIdx]} IN PROGRESS — SURVIVE AND ADVANCE`
      : "YOU'RE OUT — THE BRACKET MOVES ON";
  return (
    <section className={`playoff-bracket-section${ceremony ? " is-ceremony" : ""}`} data-testid="playoff-bracket-section">
      <span className="playoff-sweep" aria-hidden="true" />
      <div className="playoff-header">
        <span className="playoff-header-eyebrow">AL RIOTBALL LEAGUE // CHAMPIONSHIP BRACKET</span>
        <h3>ROAD TO THE CHIP</h3>
        <p data-testid="playoff-bracket-status">
          #{p.playerSeed} SEED {TEAMS[p.playerTeam]?.name}
          {p.seededByRecords ? " · SEEDED BY LIVE STANDINGS" : ""} — {status}
        </p>
      </div>
      <div className="playoff-tabs" data-testid="playoff-round-tabs">
        {TAB_LABELS.map((label, i) => (
          <button
            key={label}
            type="button"
            className={`playoff-tab${tab === i ? " is-active" : ""}`}
            onClick={() => setTab(i)}
            data-testid={`playoff-mobile-tab-${ROUND_SLUGS[i] || "champion"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {p.finished && p.champion ? (
        <motion.div
          className="playoff-ending"
          data-testid="playoff-ending-graphic"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <img
            src={`/assets/rings/ring-${p.champion}.jpg`}
            onError={(e) => { e.currentTarget.src = "/assets/riotball-champion-ending.jpg"; }}
            alt="Riotball Championship Trophy 2355 — two champions hoist the spiked core over the burning city"
          />
          <span className="playoff-ending-tag" style={{ color: TEAMS[p.champion]?.color }}>
            {p.champion === p.playerTeam ? "YOU TAKE THE CHIP" : `${TEAMS[p.champion]?.name} TAKE THE CHIP`}
          </span>
        </motion.div>
      ) : null}
      <div className="playoff-bracket" data-testid="playoff-bracket" ref={bracketRef}>
        {p.rounds.map((round, ri) => (
          <div
            key={ri}
            className={`playoff-round ${ROUND_CLASSES[ri]}${tab === ri ? " is-tab-active" : ""}`}
            data-testid={`playoff-round-${ROUND_SLUGS[ri]}`}
          >
            <span className="playoff-round-label">// {PLAYOFF_ROUNDS[ri]} <b>{ROUND_TICKS[ri]}</b></span>
            <div className="playoff-round-matches">
              {round.length ? (
                round.map((m) => <MatchCard key={m.id} m={m} liveId={live?.id} playerTeam={p.playerTeam} />)
              ) : (
                <span className="playoff-round-tbd">TBD — WINNERS ADVANCE HERE</span>
              )}
            </div>
          </div>
        ))}
        <ChampionColumn p={p} active={tab === PLAYOFF_ROUNDS.length} flames={flames} flameLabel={flameLabel} />
        {travelers.map((t) => (
          <Traveler key={t.key} t={t} playerTeam={p.playerTeam} />
        ))}
      </div>
    </section>
  );
}
